// Theme-only wishlist, saved in this browser's localStorage (no app, no
// account sync). Drives the [data-wishlist-button] hearts
// (snippets/wishlist-button.liquid), the header count [data-wishlist-count]
// and the handle-driven page [data-wishlist-page] (snippets/wishlist-page.liquid).
//
// Measurement: a saved product's add-to-cart form ([data-wishlist-form] in
// sections/product.liquid) carries the hidden line-item property `_wishlist`
// (the date it was saved, or `yes` for items saved before dates were kept),
// so orders show which purchases came through the wishlist. Adds and removes
// are also published as customer events (`wishlist_add` / `wishlist_remove`)
// with the product's details; the admin custom pixel "MARU wishlist → GA4"
// forwards them to GA4 as add_to_wishlist / remove_from_wishlist.
(() => {
  const KEY = 'maru:wishlist';
  const LIMIT = 100;

  // [{id, handle, at}] newest first. id = Shopify product id (string) and is
  // the identity; handle is only for fetching the card and can go stale; at is
  // the save date (YYYY-MM-DD), missing on items saved before it was added.
  let items = [];

  const read = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.id && item.handle) : [];
    } catch (error) {
      return [];
    }
  };

  const write = () => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
      return true;
    } catch (error) {
      return false;
    }
  };

  // Storage can be blocked (private mode, disabled site data): keep the hearts
  // hidden rather than offer a button that cannot save.
  const storageAvailable = (() => {
    try {
      localStorage.setItem(`${KEY}:probe`, '1');
      localStorage.removeItem(`${KEY}:probe`);
      return true;
    } catch (error) {
      return false;
    }
  })();

  const find = (id) => items.find((item) => item.id === id);
  const has = (id) => Boolean(find(id));

  const publish = (name, button) => {
    const {productId, variantId, title, vendor, price, currency} = button.dataset;
    try {
      window.Shopify?.analytics?.publish?.(name, {productId, variantId, title, vendor, price: Number(price), currency});
    } catch (error) {
      // Analytics must never break the heart.
    }
  };

  // Write only on change: the MutationObserver below would otherwise re-fire
  // on our own count updates forever.
  const sync = () => {
    document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      const pressed = String(has(button.dataset.productId));
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      const text = String(items.length);
      if (count.textContent !== text) count.textContent = text;
      if (count.hidden !== (items.length === 0)) count.hidden = items.length === 0;
    });
    document.querySelectorAll('[data-wishlist-form]').forEach((form) => {
      const item = find(form.dataset.wishlistForm);
      let input = form.querySelector('input[name="properties[_wishlist]"]');
      if (!item) {
        input?.remove();
        return;
      }
      const value = item.at || 'yes';
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[_wishlist]';
        form.prepend(input);
      }
      if (input.value !== value) input.value = value;
    });
  };

  const remove = (id, button) => {
    items = items.filter((item) => item.id !== id);
    write();
    sync();
    if (button) publish('wishlist_remove', button);
    document.dispatchEvent(new CustomEvent('wishlist:removed', {detail: {id}}));
  };

  const toggle = (button) => {
    const id = button.dataset.productId;
    if (has(id)) {
      remove(id, button);
      return;
    }
    const at = new Date().toLocaleDateString('sv-SE', {timeZone: 'Asia/Tokyo'});
    items = [{id, handle: button.dataset.handle, at}, ...items].slice(0, LIMIT);
    write();
    sync();
    publish('wishlist_add', button);
  };

  const handleOf = (href) => {
    const match = /\/products\/([^/?#]+)/.exec(href || '');
    return match ? decodeURIComponent(match[1]) : null;
  };

  const renderPage = (page) => {
    const grid = page.querySelector('[data-wishlist-grid]');
    const status = page.querySelector('[data-wishlist-status]');
    const empty = page.querySelector('[data-wishlist-empty]');
    const root = (page.dataset.root || '/').replace(/\/$/, '');

    const showEmptyIfNone = () => {
      empty.hidden = grid.children.length > 0;
    };

    const cards = items.map((item) =>
      fetch(`${root}/products/${encodeURIComponent(item.handle)}?section_id=wishlist-card`)
        .then((response) => {
          // Deleted or unpublished products come back as 404, or as 200 with an
          // empty section (sections/wishlist-card.liquid): drop those so the
          // header count stays honest. Other failures (offline, 5xx) keep the
          // item for next time.
          if (response.status === 404) remove(item.id);
          return response.ok ? response.text() : null;
        })
        .then((html) => {
          if (!html) return null;
          const card = new DOMParser().parseFromString(html, 'text/html').querySelector('.product-card');
          if (!card) {
            remove(item.id);
            return null;
          }
          // Renamed handles resolve through Shopify's redirect; store the new one.
          const current = handleOf(card.querySelector('.product-card__link')?.getAttribute('href'));
          if (current && current !== item.handle) {
            item.handle = current;
            write();
          }
          return card;
        })
        .catch(() => null)
    );

    Promise.all(cards).then((nodes) => {
      nodes.forEach((node) => node && grid.append(node));
      status.hidden = true;
      sync();
      showEmptyIfNone();
    });

    document.addEventListener('wishlist:removed', (event) => {
      grid.querySelector(`[data-wishlist-button][data-product-id="${event.detail.id}"]`)?.closest('.product-card')?.remove();
      showEmptyIfNone();
    });
  };

  const page = document.querySelector('[data-wishlist-page]');

  if (!storageAvailable) {
    if (page) {
      page.querySelector('[data-wishlist-status]').hidden = true;
      page.querySelector('[data-wishlist-unavailable]').hidden = false;
    }
    return;
  }

  items = read();
  document.documentElement.classList.add('wishlist-ready');
  sync();
  if (page) renderPage(page);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-button]');
    if (!button) return;
    event.preventDefault();
    toggle(button);
  });

  // Keep other open tabs in step.
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    items = read();
    sync();
  });

  // Cards inserted after load (related-style row) pick up state.
  new MutationObserver(sync).observe(document.body, {childList: true, subtree: true});
})();
